import { useState, useEffect } from 'react';
import { checkCycleRenewal, archiveOldCycleCEUs } from '../utils/cycleRenewal';
import { userService } from '../services/unifiedUserService';
import { ceuService } from '../services/unifiedCEUService';

/**
 * Hook to automatically check and renew user's certification cycle
 * @param {Object} user - Current user object
 * @param {Object} userProfile - User profile data
 * @returns {Object} - Renewal status and banner info
 */
export const useCycleRenewal = (user, userProfile) => {
  const [renewalInfo, setRenewalInfo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const checkAndRenewCycle = async () => {
      if (!user || !userProfile || isProcessing) {
        return;
      }

      // Check if renewal is needed
      const renewal = checkCycleRenewal(userProfile);
      
      if (renewal?.needsRenewal) {
        setIsProcessing(true);
        
        try {
          // Archive old cycle CEUs
          await archiveOldCycleCEUs(user.userId, renewal.oldCycleEnd, ceuService);
          
          // Update user profile with new cycle dates
          await userService.updateUser(user.userId, {
            currentCycleStart: renewal.newCycleStart,
            currentCycleEnd: renewal.newCycleEnd,
            lastCycleRenewal: renewal.renewalDate
          });
          
          // Set renewal info to show banner
          setRenewalInfo({
            newStart: renewal.newCycleStart,
            newEnd: renewal.newCycleEnd,
            renewalDate: renewal.renewalDate
          });
        } catch (error) {
          console.error('Failed to renew cycle:', error);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    checkAndRenewCycle();
  }, [user, userProfile, isProcessing]);

  const dismissBanner = () => {
    setRenewalInfo(null);
  };

  return {
    renewalInfo,
    dismissBanner,
    isProcessing
  };
};
